/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { BillingApiService } from '../../../http';
import { CheckoutService } from '../../../services/component';
import { Component, Input, OnInit } from '@angular/core';
import { ErrorResponseModel, NotificationModel } from '../../../../../../core/models';
import { LogService, StripeService, UtilsService } from '../../../../../../core/services';
import { NotificationEnum } from '../../../../../../core/enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-select-product',
    templateUrl: './select-product.component.html',
    styleUrls: ['./select-product.component.scss']
})
export class SelectProductComponent extends BaseComponent implements OnInit {
    /**
     * Optional product id.
     */
    @Input() public priceId: string = null;

    /**
     * Optional product interval.
     */
    @Input() public interval: string = 'year';

    /**
     * Step index set by parent.
     */
    @Input() public stepIndex: number;

    /**
     * Option to show all products.
     */
    @Input() public forceShowAllProducts: boolean = true;

    /**
     * Active state from the checkout service.
     */
    public activeStep: number = null;

    /**
     * All products.
     */
    public products: any = [];

    /**
     * Used in the view loop to store the last product type.
     */
    public lastProductType: string = null;

    /**
     * Store cart has portal subscription for view.
     */
    public cartHasPortalSubscription: boolean = false;

    /**
     * Store the interval of the item in the cart (you can't mix the interval type in a single subscription).
     */
    public cartInterval: 'month' | 'year' = null;

    /**
     * Set if the cart has reached max subscription amount.
     */
    public cartMaxItemsReached: boolean = false;

    /**
     * Cart reached max number of items notification.
     */
    public notification: NotificationModel = {
        closeButton: false,
        date: new Date(),
        hidden: false,
        hideTypeMessage: true,
        id: 'cartMaxItemsReached',
        level: NotificationEnum.WARNING,
        message: 'checkout_cart_max-items_message',
        messageTranslate: true,
        pageReload: false,
        title: '',
        type: 'general',
        url: ''
    };

    /**
     * Store the selected price id.
     */
    public selectedPriceId: any[] = [];

    /**
     * Is loading flag
     * @private
     */
    private isLoading: boolean = true;

    constructor(
        private billingApiService: BillingApiService,
        private checkoutService: CheckoutService,
        private logService: LogService,
        private stripeService: StripeService,
        private utilsService: UtilsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setUpPage();
        this.subscribeToCheckoutService();

        this.activeStep = this.checkoutService.activeStep;
    }

    get cartItems(): any[] {
        return this.checkoutService.cartItems;
    }

    /**
     * Set step in checkout.
     */
    public setStep(): void {
        this.checkoutService.setStep(this.stepIndex);
    }

    /**
     * Set the selected interval.
     * @param priceId
     * @param index
     */
    public setSelectedInterval(priceId: string, index: number): void {
        this.selectedPriceId[index] = priceId;
    }

    /**
     * Format price.
     * @param price
     */
    public formatPrice(price: any): string {
        const symbol: string = this.stripeService.getCurrencySymbol(price.currency);

        return symbol + this.stripeService.getUnitPrice(price.unitAmount);
    }

    /**
     * Set the last category in the loop to change the view.
     * @param productType
     */
    public setLastProductType(productType: string): void {
        this.lastProductType = productType;
    }

    /**
     * Set the selected product in the cart.
     * @param product
     * @param index
     */
    public addProductToCart(product: any, index: number): void {
        const selectedPriceId: any = this.selectedPriceId[index] ? this.selectedPriceId[index] : this.getAnnualPriceId(product);
        const price: any = product.prices.find((item: any): boolean => item.id === selectedPriceId);
        if (this.isLoading || this.activeStep === this.stepIndex) {
            if (product['product_type'] === 'portal' && this.cartContainsPortalSubscription(this.checkoutService.cartItems)) {
                this.logService.warn('SelectProductComponent:setSelectedProduct', 'Cart already contains a subscription');
            } else {
                this.checkoutService.cartItems = {
                    product: product,
                    price,
                    index,
                    quantity: 1
                };

                this.cartInterval = price.interval;
            }
        }

        this.cartInterval = price?.interval;
    }

    /**
     * Delete cart item.
     * @param selectedProduct
     */
    public deleteCartItem(selectedProduct: any): void {
        this.checkoutService.deleteCartItem(selectedProduct);
    }

    /**
     * Get annual price id.
     * @param product
     * @private
     */
    private getAnnualPriceId(product: any): string {
        const price: any = product.prices.find((item: any): boolean => item.interval === 'year');
        if (!price) {
            this.logService.debug('PurchaseProductComponent:getAnnualPriceId', product);

            return;
        }

        return price.id;
    }

    /**
     * Set up page
     * @private
     */
    private setUpPage(): void {
        this.billingApiService.getAllProducts()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.products = this.populateProducts(data, this.priceId);
                    this.checkCartStatus();
                    this.isLoading = false;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SelectProductComponent:setUpPage', error.message);
                }
            });
    }

    /**
     * Populate products.
     * @param products
     * @param priceId
     * @private
     */
    private populateProducts(products: any, priceId?: string): any[] {
        const sortedProducts: any = this.utilsService.sortArrayByProperty(products, 'product_type', ['portal', 'adc_pack']);
        const defaultInterval = this.findProductInterval(products, priceId);
        const excludedProducts: number[] = [
            8, // Portal Trial
            11 // ADC Portal Free
        ];

        const formattedProducts: any = [];
        let index: number = 0;
        for (const product of sortedProducts) {
            if (!excludedProducts.includes(product.id)) {
                product.prices = this.utilsService.sortArrayByProperty(product?.prices, 'interval', ['month', 'year']);
                product.id = product['stripe_product_id'];
                delete product['stripe_product_id'];

                const priceIndex: any = product.prices.findIndex((price: any): boolean => price.id === priceId);
                if (this.forceShowAllProducts && priceIndex === -1) {
                    const index: number = this.getPriceIndex(product, defaultInterval);
                    this.selectedPriceId.push(product.prices[index].id);
                } else {
                    this.selectedPriceId.push(product.prices[priceIndex].id);
                    this.addProductToCart(product, index);
                }

                if (!this.forceShowAllProducts && product['product_type'] === 'portal') {
                    product.hidden = true;
                }

                formattedProducts.push(product);

                index++;
            }
        }

        return formattedProducts;
    }

    /**
     * Find incoming product's interval.
     * @param products
     * @param priceId
     * @private
     */
    private findProductInterval(products: any[], priceId: string) {
        return products.reduce((interval: any, item: { prices: any[]; }) => {
            const matchIndex: number = item.prices.findIndex(item => item.id === priceId);
            if (matchIndex !== -1) {
                const caughtValue: any = item.prices[matchIndex]?.interval;
                if (caughtValue) {
                    interval = caughtValue;
                }
            }

            return interval;
        });
    }

    /**
     * Get price index.
     * @param product
     * @param defaultInterval
     * @private
     */
    private getPriceIndex(product: any, defaultInterval: string): number {
        const priceIndex = product.prices.findIndex((price: any): boolean => price.interval === defaultInterval);

        return defaultInterval && priceIndex !== -1 ? priceIndex : 0;
    }

    /**
     * Subscribe to check out service.
     * @private
     */
    private subscribeToCheckoutService(): void {
        this.checkoutService.activeStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                    if (this.activeStep === this.stepIndex) {
                        this.checkCartStatus();
                    }
                }
            });

        this.checkoutService.cartItemsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response): void => {
                    const cartItems = this.checkoutService.cartItems;
                    this.cartHasPortalSubscription = this.cartContainsPortalSubscription(cartItems);
                    this.cartMaxItemsReached = this.getMaxCartItemsReached(cartItems);
                    if (!response) {
                        this.cartInterval = null;
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn('CentralisedManagementComponent: setUpCartTotal', 'Unable to get cart total.', error.message);
                }
            });
    }

    /**
     * Check cart status.
     * @private
     */
    private checkCartStatus(): void {
        const cartItems: any[] = this.checkoutService.cartItems;
        if (cartItems.length) {
            const timerId = setTimeout((): void => {
                this.checkoutService.isStepValidEmitter.next(true);
                this.cartHasPortalSubscription = this.cartContainsPortalSubscription(cartItems);
                this.cartMaxItemsReached = this.getMaxCartItemsReached(cartItems);
                this.cartInterval = cartItems.length ? cartItems[0].price.interval : null;
                clearTimeout(timerId);
            }, 0);
        }
    }

    /**
     * Check cart items for portal subscription.
     * @param cartItems
     * @private
     */
    private cartContainsPortalSubscription(cartItems: any[]): boolean {
        return cartItems.length > 0 && cartItems?.some((item: any): boolean => item.product['product_type'] === 'portal');
    }

    /**
     * Set max cart items flag.
     * @param cartItems
     * @private
     */
    private getMaxCartItemsReached(cartItems: any[]): boolean {
        const quantities: number [] = cartItems ? cartItems.map((item: any): any => item.quantity) : [0];
        if (!quantities?.length) {
            return false;
        }

        const totalItems: number = quantities.reduce((totalItems: number, quantity: number): number => totalItems + quantity);

        return totalItems >= 20;
    }
}
